import React, { useMemo, useRef, useState } from 'react';
import { Send, Paperclip, Lock, AlertTriangle, Zap, Ban } from 'lucide-react';
import {
  CannedReply,
  Conversation,
  Template,
  expandReplyBody
} from '../../../types/models/whatsapp';
import CannedReplyPalette, { filterReplies } from './CannedReplyPalette';

interface ComposerProps {
  conversation: Conversation;
  windowOpen: boolean;
  draft: string;
  cannedReplies: CannedReply[];
  agentName: string;
  /** Set when the contact blocked or opted out — no send is possible at all. */
  sendBlockedReason?: string | null;
  onDraftChange: (text: string) => void;
  onSendText: (text: string) => Promise<void>;
  onSendTemplate: (template: Template, variables: Record<string, string>) => Promise<void>;
  onOpenTemplatePicker: () => void;
  onAttach: (file: File) => Promise<void>;
  onCannedReplyUsed?: (replyId: string) => void;
}

/**
 * The palette opens only when '/' begins the draft.
 *
 * Matching anywhere in the text would fire on ordinary messages containing a
 * slash (URLs, dates, "and/or"), which is far more common than the shortcut.
 */
const SHORTCUT_PATTERN = /^\/([\w-]*)$/;

/**
 * The composer is where the 24h window rule becomes visible.
 *
 * When the window is closed we do not merely disable the send button — we
 * replace the input entirely, so an agent can never type a message that is
 * guaranteed to be rejected by Meta.
 */
const Composer: React.FC<ComposerProps> = ({
  conversation,
  windowOpen,
  draft,
  cannedReplies,
  agentName,
  sendBlockedReason,
  onDraftChange,
  onSendText,
  onOpenTemplatePicker,
  onAttach,
  onCannedReplyUsed
}) => {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paletteIndex, setPaletteIndex] = useState(0);
  /** Set when Esc dismisses the palette, so it stays closed while typing. */
  const [paletteDismissed, setPaletteDismissed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const shortcutMatch = draft.match(SHORTCUT_PATTERN);
  const paletteOpen =
    !paletteDismissed && shortcutMatch !== null && cannedReplies.length > 0;
  const paletteFilter = shortcutMatch?.[1] ?? '';

  const matches = useMemo(
    () => (paletteOpen ? filterReplies(cannedReplies, paletteFilter) : []),
    [paletteOpen, cannedReplies, paletteFilter]
  );

  const insertReply = (reply: CannedReply) => {
    const body = expandReplyBody(reply.body, {
      contactName: conversation.contactName,
      contactPhone: conversation.contactPhone,
      agentName
    });

    onDraftChange(body);
    onCannedReplyUsed?.(reply.id);
    setPaletteIndex(0);

    // Return focus and drop the caret at the end, ready to edit before sending.
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(body.length, body.length);
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    });
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;

    setSending(true);
    setError(null);
    try {
      await onSendText(text);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // While the palette is open it owns the navigation keys, so Enter selects a
    // reply rather than sending a message that is still just "/short".
    if (paletteOpen && matches.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setPaletteIndex((index) => (index + 1) % matches.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setPaletteIndex((index) => (index - 1 + matches.length) % matches.length);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        insertReply(matches[Math.min(paletteIndex, matches.length - 1)]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setPaletteDismissed(true);
        return;
      }
    }

    // Enter sends, Shift+Enter inserts a newline — the WhatsApp convention.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target;
    onDraftChange(value);

    // A fresh '/' re-arms the palette after a previous Esc.
    if (!value.startsWith('/')) setPaletteDismissed(false);
    setPaletteIndex(0);

    const el = event.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    try {
      await onAttach(file);
    } catch (err: any) {
      setError(err.message || 'Failed to upload attachment');
    } finally {
      event.target.value = '';
    }
  };

  // Takes precedence over the window state: a template cannot reopen a
  // conversation with a contact who has opted out or been blocked.
  if (sendBlockedReason) {
    return (
      <div className="border-t border-gray-200 bg-danger-50 p-4">
        <div className="flex items-center gap-3">
          <Ban className="h-5 w-5 shrink-0 text-danger-500" />
          <p className="flex-1 text-sm text-danger-700">
            {sendBlockedReason}
          </p>
        </div>
      </div>
    );
  }

  if (!windowOpen) {
    return (
      <div className="border-t border-gray-200 bg-gray-50 p-4">
        <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:text-left">
          <Lock className="h-5 w-5 shrink-0 text-gray-400" />
          <p className="flex-1 text-sm text-gray-600">
            <span className="font-medium">24-hour window closed.</span>{' '}
            Send an approved template to reopen this conversation.
          </p>
          <button
            type="button"
            onClick={onOpenTemplatePicker}
            className="shrink-0 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
          >
            Choose template
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative border-t border-gray-200 bg-white p-3">
      {paletteOpen && (
        <div className="absolute bottom-full left-3 right-3 z-20">
          <CannedReplyPalette
            replies={cannedReplies}
            filter={paletteFilter}
            activeIndex={paletteIndex}
            onHover={setPaletteIndex}
            onSelect={insertReply}
          />
        </div>
      )}

      {error && (
        <div className="mb-2 flex items-start gap-2 rounded-lg bg-danger-50 p-2 text-xs text-danger-600">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx"
          onChange={handleFileChange}
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="shrink-0 rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100"
          aria-label="Attach file"
        >
          <Paperclip className="h-5 w-5" />
        </button>

        {cannedReplies.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setPaletteDismissed(false);
              setPaletteIndex(0);
              // Prefixing with '/' is what opens the palette.
              if (!draft.startsWith('/')) onDraftChange('/');
              textareaRef.current?.focus();
            }}
            className="shrink-0 rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100"
            aria-label="Canned replies"
            title="Canned replies (type / )"
          >
            <Zap className="h-5 w-5" />
          </button>
        )}

        <button
          type="button"
          onClick={onOpenTemplatePicker}
          className="shrink-0 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
        >
          Template
        </button>

        <textarea
          ref={textareaRef}
          rows={1}
          value={draft}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          placeholder="Type a message…"
          className="max-h-40 flex-1 resize-none rounded-2xl border-gray-300 px-4 py-2 text-[15px] focus:border-primary-500 focus:ring-primary-500"
        />

        <button
          type="button"
          onClick={handleSend}
          disabled={!draft.trim() || sending}
          className="shrink-0 rounded-full bg-wa-green p-2.5 text-white transition-colors hover:bg-wa-teal disabled:cursor-not-allowed disabled:bg-gray-300"
          aria-label="Send message"
        >
          <Send className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default Composer;
